import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { t, getal, wanneer, geleden } from '../i18n.js';
import { St } from '../components/Status.jsx';
import Gemaskeerd from '../components/Gemaskeerd.jsx';

/**
 * De pagina **Secure Connect** (docs/ontwerp/duo.md): de authenticaties en het beheer uit Secure Connect (Cisco Duo),
 * te zoeken op gebruiker of adres. De alerts staan bij Alerts (module Secure Connect); hier is het waarom erachter.
 */
const UITKOMST = { success: 'goedgekeurd', denied: 'geweigerd', fraud: 'fraude', failure: 'mislukt', error: 'fout' };
const soortVan = (r) => (r.result === 'success' ? 'good' : r.result === 'fraud' || r.reason === 'user_marked_fraud' ? 'critical' : 'serious');

export default function Duo({ klant }) {
  const [tekst, zetTekst] = useState('');
  const [zoek, zetZoek] = useState('');
  useEffect(() => { const h = setTimeout(() => zetZoek(tekst.trim()), 300); return () => clearTimeout(h); }, [tekst]);
  const q = useQuery({ queryKey: ['duo', klant, zoek], queryFn: () => api.duo(klant, zoek), refetchInterval: 60_000 });
  const kop = (
    <div className="page-head"><div>
      <h1>Secure Connect</h1>
      <p className="sub">{t('Inloggen met een tweede stap (Secure Connect)')}</p>
    </div></div>
  );
  if (q.isPending) return <>{kop}<p className="muted">{t('Laden…')}</p></>;
  if (q.isError) return <>{kop}<div className="melder rood">{t(q.error.message)}</div></>;
  const { accounts, counts, logins, admin } = q.data.data;
  if (!accounts.length) {
    return <>{kop}<section className="card"><p className="empty">{t('Nog geen Secure Connect in ADTS ZERO. Stel de koppeling in onder Beheer › Secure Connect en koppel het subaccount aan de klant.')}</p>
      <div className="actions"><Link className="btn" to="/beheer">{t('Naar Beheer')}</Link></div></section></>;
  }
  const tegel = (n, label, extra = '') => <div className={`tile ${extra}`}><div className="n">{getal(n)}</div><div className="t">{label}</div></div>;
  return (
    <>
      {kop}
      <div className="tiles">
        {tegel(counts.logins, t('logins in 24 uur'))}
        {tegel(counts.geweigerd, t('geweigerd'), counts.geweigerd ? 'warn' : '')}
        {tegel(counts.fraude, t('gemeld als fraude'), counts.fraude ? 'warn' : '')}
        {tegel(counts.bypass, t('met een bypass-code'))}
      </div>
      <section className="card" style={{ marginBottom: 18 }}>
        <header><h2>{t('Authenticaties')}</h2>
          <input type="search" className="search" value={tekst} onChange={(e) => zetTekst(e.target.value)} maxLength={100}
            placeholder={t('Zoek op gebruiker of adres')} aria-label={t('Zoek op gebruiker of adres')} /></header>
        {!logins.length ? <p className="empty">{zoek ? t('Niets gevonden voor {zoek}.', { zoek }) : t('Nog geen authenticaties uit Secure Connect.')}</p> : (
          <table>
            <thead><tr><th>{t('Wanneer')}</th><th>{t('Gebruiker')}</th><th>{t('Uitkomst')}</th><th className="hide-sm">{t('Hoe')}</th>
              <th className="hide-sm">{t('Toepassing')}</th><th className="hide-sm">{t('Vanaf')}</th></tr></thead>
            <tbody>{logins.map((r, i) => (
              <tr key={i}>
                <td>{wanneer(r.at)}</td>
                <td className="mono"><Gemaskeerd veld={r.user_name} mono /></td>
                <td><St soort={soortVan(r)}>{t(UITKOMST[r.result] ?? r.result ?? '—')}</St>{r.reason && <><br /><span className="muted">{r.reason.replace(/_/g, ' ')}</span></>}</td>
                <td className="hide-sm">{(r.factor ?? '—').replace(/_/g, ' ')}{r.device && <><br /><span className="muted">{r.device}</span></>}</td>
                <td className="hide-sm">{r.application ?? '—'}</td>
                <td className="hide-sm"><Gemaskeerd veld={r.ip} mono />{(r.city || r.country) && <><br /><span className="muted">{[r.city, r.country].filter(Boolean).join(', ')}</span></>}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </section>
      <div className="grid-2">
        <section className="card">
          <header><h2>{t('Beheer in Secure Connect')}</h2><span className="meta">{t('laatste 30 dagen')}</span></header>
          {!admin.length ? <p className="empty">{t('Geen beheer.')}</p> : (
            <div className="rows">{admin.map((e, i) => (
              <div className="row" key={i} style={{ gridTemplateColumns: '150px minmax(0,1fr)' }}>
                <span className="t">{wanneer(e.at)}</span>
                <span><b className="mono"><Gemaskeerd veld={e.username} mono /></b> · {e.action.replace(/_/g, ' ')}{e.object && <span className="muted"> · <Gemaskeerd veld={e.object} /></span>}</span>
              </div>
            ))}</div>
          )}
        </section>
        <section className="card">
          <header><h2>{t('Subaccounts')}</h2></header>
          <table>
            <thead><tr><th>Duo</th><th>{t('Klant')}</th><th>{t('Laatst opgehaald')}</th></tr></thead>
            <tbody>{accounts.map((a) => (
              <tr key={a.name}><td>{a.name}</td><td>{a.klant}</td>
                <td>{a.sync_error ? <St soort="critical">{t('fout')}</St> : a.synced_at ? geleden(a.synced_at) : t('nog niet')}</td></tr>
            ))}</tbody>
          </table>
        </section>
      </div>
    </>
  );
}
